import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { apiFetch } from '../../utils/apiFetch';
import { showToast } from '../../utils/toast';
import { PageLayout } from '../../components/DesignSystem';
import { Button, Input, Select } from 'antd';
import './Customerview.css';

function normalizeCustomerType(value: unknown): string {
  const v = String(value || 'Retail');
  if (v === 'Wholesale' || v === 'Walk-in') return v;
  return 'Retail';
}

function formatTags(tags: unknown): string[] {
  if (Array.isArray(tags)) return tags.map(String);
  if (typeof tags === 'string') {
    try {
      const parsed = JSON.parse(tags);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch { /* plain string */ }
    return tags ? [tags] : [];
  }
  return [];
}

const Field = ({ label, value, highlight, link }: { label: string; value: any; highlight?: string; link?: string }) => (
  <div style={{ marginBottom: 18 }}>
    <span style={s.fieldLabel}>{label}</span>
    {link ? (
      <a href={link.startsWith('mailto:') ? link : link} target={link.startsWith('mailto:') ? undefined : '_blank'} rel="noreferrer"
        style={{ fontSize: 15, color: '#0074d9', fontWeight: 500, textDecoration: 'none', wordBreak: 'break-word' }}>
        {value || '—'}
      </a>
    ) : (
      <span style={{ fontSize: 15, color: highlight || '#111827', fontWeight: highlight ? 700 : 500, wordBreak: 'break-word' }}>
        {value ?? '—'}
      </span>
    )}
  </div>
);

const StatCard = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <div style={s.statCard}>
    <div style={s.statLabel}>{label}</div>
    <div style={s.statValue}>{value}</div>
    {sub && <div style={s.statSub}>{sub}</div>}
  </div>
);

const CustomerView: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<any>(null);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [creditBalance, setCreditBalance] = useState<number>(0);
  const { token } = useAuth();

  const [contractPrices, setContractPrices] = useState<any[]>([]);
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [cpProductId, setCpProductId] = useState('');
  const [cpPrice, setCpPrice] = useState('');
  const [cpNote, setCpNote] = useState('');
  const [cpSaving, setCpSaving] = useState(false);
  const [portalInviting, setPortalInviting] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      navigate('/login');
      return;
    }

    apiFetch(`${API_URL}/customers/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : Promise.reject(`HTTP ${res.status}`))
      .then(data => {
        setCustomer({
          ...data,
          companyName: data.companyName || data.companyname || '',
          companyNumber: data.companyNumber || data.companynumber || '',
          website: data.website || '',
          sicCode: data.sicCode || data.siccode || '',
          digitalId: data.digitalId || data.digital_id || '',
          customerType: normalizeCustomerType(data.customer_type || data.customerType || data.customertype),
          pricingTier: data.pricing_tier || data.pricingTier || '',
          vatNumber: data.vat_number || data.vatNumber || '',
          creditTerms: data.credit_terms ?? data.creditTerms ?? 30,
          creditLimit: data.credit_limit ?? data.creditLimit,
          sendMonthlyStatement: data.send_monthly_statement || false,
          statementEmailOverride: data.statement_email_override || '',
          tags: formatTags(data.tags),
        });
      })
      .catch(() => setCustomer(null))
      .finally(() => setLoading(false));

    apiFetch(`${API_URL}/customers/${id}/attachments`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : [])
      .then(list => setAttachments(Array.isArray(list) ? list : []))
      .catch(() => setAttachments([]));

    apiFetch(`${API_URL}/credit-notes/customer/${id}/balance`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : { credit_balance: 0 })
      .then(data => setCreditBalance(parseFloat(data.credit_balance) || 0))
      .catch(() => setCreditBalance(0));

    apiFetch(`${API_URL}/customers/${id}/prices`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(list => setContractPrices(Array.isArray(list) ? list : []))
      .catch(() => setContractPrices([]));

    apiFetch(`${API_URL}/products`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setAllProducts(Array.isArray(data) ? data : (data?.products || [])))
      .catch(() => setAllProducts([]));
  }, [id, token, navigate]);

  const saveContractPrice = async () => {
    if (!cpProductId || !cpPrice) { showToast('Select a product and enter a price'); return; }
    const price = parseFloat(cpPrice);
    if (Number.isNaN(price) || price < 0) { showToast('Enter a valid price'); return; }
    setCpSaving(true);
    try {
      const res = await apiFetch(`${API_URL}/customers/${id}/prices/${cpProductId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ price, note: cpNote }),
      });
      if (res.ok) {
        const updated = await res.json();
        setContractPrices(prev => {
          const existing = prev.findIndex(p => p.product_id === parseInt(cpProductId, 10));
          const prod = allProducts.find((p: any) => String(p.id || p._id) === cpProductId);
          const row = { ...updated, product_name: prod?.name || '', list_price: prod?.price || 0, wholesale_price: prod?.wholesale_price };
          return existing >= 0 ? prev.map((p, i) => (i === existing ? row : p)) : [...prev, row];
        });
        setCpProductId('');
        setCpPrice('');
        setCpNote('');
        showToast('Contract price saved');
      } else {
        showToast('Failed to save contract price');
      }
    } finally {
      setCpSaving(false);
    }
  };

  const deleteContractPrice = async (productId: number) => {
    if (!window.confirm('Remove this contract price?')) return;
    await apiFetch(`${API_URL}/customers/${id}/prices/${productId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    setContractPrices(prev => prev.filter(p => p.product_id !== productId));
    showToast('Contract price removed');
  };

  const archiveCustomer = () => {
    if (!window.confirm(`Archive "${customer?.name}"?`)) return;
    if (!token) {
      showToast('Please log in to archive customers');
      navigate('/login');
      return;
    }
    apiFetch(`${API_URL}/customers/${id}/archive`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (res.ok) {
          showToast('Customer archived');
          navigate('/customers/list');
        } else {
          res.json().then(e => showToast(`Failed to archive: ${e.message || 'Unknown error'}`));
        }
      })
      .catch(() => showToast('Network error during archiving'));
  };

  const sendPortalInvite = async () => {
    if (!customer?.email) {
      showToast('Add an email address before sending a portal invite');
      return;
    }
    setPortalInviting(true);
    try {
      const res = await apiFetch(`${API_URL}/customers/${id}/portal-invite`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        showToast(data.message || 'Portal invitation sent');
        setCustomer((prev: any) => prev ? { ...prev, portal_status: 'invited', portal_invited_at: new Date().toISOString() } : prev);
      } else {
        showToast(data.error || 'Failed to send invitation');
      }
    } catch {
      showToast('Failed to send invitation');
    } finally {
      setPortalInviting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 80, textAlign: 'center', color: '#6b7280' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
        Loading customer…
      </div>
    );
  }

  if (!customer) {
    return (
      <div style={{ padding: 80, textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>❌</div>
        <p style={{ color: '#374151', fontWeight: 600 }}>Customer not found</p>
        <Button style={{ marginTop: 16 }} onClick={() => navigate('/customers/list')}>← Back to customers</Button>
      </div>
    );
  }

  const status = customer.status || 'Active';

  const imageUrl = customer.image || '';
  const resolvedImage = imageUrl
    ? (imageUrl.startsWith('http') ? imageUrl : `${API_URL.replace('/api', '')}${imageUrl}`)
    : '';
  const initial = (customer.name || '?')[0].toUpperCase();
  const creditTerms = customer.creditTerms ?? customer.credit_terms ?? 30;
  const creditLimit = customer.creditLimit ?? customer.credit_limit;
  const customerType = normalizeCustomerType(customer.customerType);
  const typePillClass =
    customerType === 'Wholesale' ? 'cv-type-pill cv-type-pill--wholesale'
      : customerType === 'Walk-in' ? 'cv-type-pill cv-type-pill--walkin'
        : 'cv-type-pill cv-type-pill--retail';
  const fmtDate = (v: any) => (v ? new Date(v).toLocaleString('en-GB') : '—');
  const fmtMoney = (v: any) => (v != null && v !== '' ? `£${Number(v).toFixed(2)}` : 'Not set');
  const portalStatus = customer.portal_status || 'not_invited';
  const portalLabel =
    portalStatus === 'active' ? 'Portal active'
      : portalStatus === 'invited' ? 'Invited — awaiting signup'
        : portalStatus === 'invite_expired' ? 'Invite expired'
          : 'Not invited';
  const shopUrl = customer.business_id ? `${window.location.origin}/shop/${customer.business_id}` : '';

  return (
    <PageLayout
      title={customer.name}
      action={
        <div className="cv-actions">
          <button type="button" className="cv-btn cv-btn--order" onClick={() => navigate(`/orders/new?customer_id=${id}`)}>New order</button>
          <button type="button" className="cv-btn cv-btn--invoice" onClick={() => navigate(`/invoices/add?customer_id=${id}`)}>New invoice</button>
          <button type="button" className="cv-btn cv-btn--history" onClick={() => navigate(`/customers/view/${id}/orders`)}>Order history</button>
          <button type="button" className="cv-btn cv-btn--edit" onClick={() => navigate(`/customers/edit/${id}`)}>✏️ Edit</button>
          <button type="button" className="cv-btn cv-btn--archive" onClick={archiveCustomer}>Archive</button>
          <button type="button" className="cv-btn cv-btn--back" onClick={() => navigate('/customers/list')}>Back</button>
        </div>
      }
    >
      <div style={s.card}>
        <div style={s.hero}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
            {resolvedImage ? (
              <img src={resolvedImage} alt={customer.name} style={s.bannerAvatar} />
            ) : (
              <div style={s.bannerAvatarFallback}>{initial}</div>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 20, color: '#111827', lineHeight: 1.2 }}>{customer.name}</div>
              {customer.companyName && (
                <div style={{ color: '#6b7280', fontSize: 14, marginTop: 4 }}>{customer.companyName}</div>
              )}
              <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 6 }}>
                #{customer.id || customer._id}
                {customer.pricingTier ? ` · Tier ${customer.pricingTier}` : ''}
              </div>
              <span className={typePillClass}>{customerType}</span>
            </div>
          </div>
          <span style={{ ...s.statusPill, background: status === 'Active' ? '#dcfce7' : status === 'Inactive' ? '#fef3c7' : '#f3f4f6', color: status === 'Active' ? '#166534' : status === 'Inactive' ? '#92400e' : '#6b7280' }}>{status}</span>
        </div>

        {/* Account classification — mirrors edit form */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Account classification</h3>
          <div style={s.grid}>
            <Field label="Customer type" value={customerType} highlight={customerType === 'Wholesale' ? '#0074d9' : undefined} />
            <Field label="Status" value={status} />
            <Field label="Pricing tier" value={customer.pricingTier ? `Tier ${customer.pricingTier}` : 'Standard (wholesale price)'} />
            <Field label="Credit terms" value={`Net ${creditTerms}`} />
            <Field label="Credit limit" value={fmtMoney(creditLimit)} />
            <Field
              label="Monthly statement"
              value={customer.sendMonthlyStatement ? 'Enabled — emailed on the 1st' : 'Off'}
            />
            <Field
              label="Statement email"
              value={customer.statementEmailOverride || customer.email || 'Uses primary email'}
            />
          </div>
        </div>

        <div style={s.section}>
          <h3 style={s.sectionTitle}>B2B shop portal</h3>
          <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b7280' }}>
            Send a secure link so this customer can log in, place orders, and see {customerType === 'Wholesale' ? 'trade' : 'their'} pricing. Their account name here stays under your control.
          </p>
          <div style={s.grid}>
            <Field label="Portal status" value={portalLabel} highlight={portalStatus === 'active' ? '#166534' : undefined} />
            <Field label="Invited" value={customer.portal_invited_at ? fmtDate(customer.portal_invited_at) : '—'} />
            {shopUrl && <Field label="Shop URL" value={shopUrl} link={shopUrl} />}
          </div>
          {customer.email && portalStatus !== 'active' && (
            <div style={{ marginTop: 14 }}>
              <Button type="primary" loading={portalInviting} onClick={sendPortalInvite}>
                {portalStatus === 'invited' ? 'Resend portal invitation' : 'Send portal invitation'}
              </Button>
            </div>
          )}
          {!customer.email && (
            <p style={{ marginTop: 12, fontSize: 13, color: '#b45309' }}>Add an email on this customer before sending a portal invite.</p>
          )}
        </div>

        {/* Quick stats */}
        <div style={s.statsRow}>
          <StatCard label="Credit balance" value={`£${creditBalance.toFixed(2)}`} sub="Available credit notes" />
          <StatCard label="Payment terms" value={`Net ${creditTerms}`} sub="Standard invoice terms" />
          <StatCard label="Credit limit" value={fmtMoney(creditLimit)} />
          <StatCard
            label="Account type"
            value={customerType}
            sub={customer.pricingTier ? `Pricing tier ${customer.pricingTier}` : 'Standard pricing'}
          />
        </div>

        {/* Contact */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Contact &amp; company</h3>
          <div style={s.grid}>
            <Field label="Company name" value={customer.companyName} />
            <Field label="Email" value={customer.email} link={customer.email ? `mailto:${customer.email}` : undefined} />
            <Field label="Phone" value={customer.phone} link={customer.phone ? `tel:${customer.phone}` : undefined} />
            <Field
              label="Website"
              value={customer.website}
              link={customer.website ? (String(customer.website).startsWith('http') ? customer.website : `https://${customer.website}`) : undefined}
            />
            <Field label="Company number" value={customer.companyNumber} />
            <Field label="VAT number" value={customer.vatNumber || customer.vat_number} />
            <Field label="SIC code" value={customer.sicCode} />
            <Field label="Digital ID" value={customer.digitalId} />
          </div>
        </div>

        {/* Address — always show section like the form */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Address</h3>
          <div style={s.grid}>
            <Field label="Street" value={customer.address} />
            <Field label="City" value={customer.city} />
            <Field label="Postcode" value={customer.postcode} />
            <Field label="Country" value={customer.country} />
          </div>
        </div>

        {/* Notes — show section even when empty */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Notes</h3>
          {customer.notes ? (
            <div style={s.notesBox}>{customer.notes}</div>
          ) : (
            <p style={s.emptyHint}>No notes recorded for this customer.</p>
          )}
        </div>

        {/* Tags */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Tags</h3>
          {customer.tags?.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {customer.tags.map((tag: string, index: number) => (
                <span key={index} style={s.tag}>{tag}</span>
              ))}
            </div>
          ) : (
            <p style={s.emptyHint}>No tags — add them when editing this customer.</p>
          )}
        </div>

        {/* Attachments — moved below notes/tags to match form flow */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Attachments</h3>
          {attachments.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {attachments.map((att: any) => (
                <a key={att.id} href={att.url} target="_blank" rel="noopener noreferrer" style={s.attachmentLink}>
                  📎 {att.filename || att.name || 'Attachment'}
                </a>
              ))}
            </div>
          ) : (
            <p style={s.emptyHint}>No attachments uploaded for this customer.</p>
          )}
        </div>

        {/* Contract prices */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Contract / agreed prices</h3>
          <p style={s.sectionHint}>
            Override list and wholesale prices when creating orders and invoices for this customer.
          </p>

          <div style={s.contractForm}>
            <div style={{ flex: '1 1 220px' }}>
              <label style={s.formLabel}>Product</label>
              <Select
                showSearch
                placeholder="Select product"
                value={cpProductId || undefined}
                onChange={v => setCpProductId(v)}
                style={{ width: '100%' }}
                optionFilterProp="label"
                options={allProducts.map((p: any) => ({
                  value: String(p.id || p._id),
                  label: p.name,
                }))}
              />
            </div>
            <div style={{ flex: '0 0 130px' }}>
              <label style={s.formLabel}>Agreed price (£)</label>
              <Input type="number" min={0} step={0.01} placeholder="0.00" value={cpPrice} onChange={e => setCpPrice(e.target.value)} />
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <label style={s.formLabel}>Note (optional)</label>
              <Input placeholder="e.g. 12-month agreement" value={cpNote} onChange={e => setCpNote(e.target.value)} />
            </div>
            <Button type="primary" loading={cpSaving} onClick={saveContractPrice} style={{ alignSelf: 'flex-end', fontWeight: 600 }}>
              Save price
            </Button>
          </div>

          {contractPrices.length === 0 ? (
            <p style={s.emptyHint}>No contract prices set yet.</p>
          ) : (
            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={s.th}>Product</th>
                    <th style={s.th}>List</th>
                    <th style={s.th}>Wholesale</th>
                    <th style={{ ...s.th, color: '#0074d9' }}>Contract</th>
                    <th style={s.th}>Saving</th>
                    <th style={s.th}>Note</th>
                    <th style={s.th} />
                  </tr>
                </thead>
                <tbody>
                  {contractPrices.map((cp: any) => {
                    const saving = cp.list_price ? cp.list_price - cp.price : null;
                    return (
                      <tr key={cp.product_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={s.td}>{cp.product_name}</td>
                        <td style={s.td}>£{parseFloat(cp.list_price || 0).toFixed(2)}</td>
                        <td style={s.td}>{cp.wholesale_price ? `£${parseFloat(cp.wholesale_price).toFixed(2)}` : '—'}</td>
                        <td style={{ ...s.td, fontWeight: 700, color: '#0074d9' }}>£{parseFloat(cp.price).toFixed(2)}</td>
                        <td style={{ ...s.td, color: saving && saving > 0 ? '#16a34a' : '#6b7280' }}>
                          {saving && saving > 0 ? `£${saving.toFixed(2)} off` : '—'}
                        </td>
                        <td style={{ ...s.td, color: '#6b7280' }}>{cp.note || '—'}</td>
                        <td style={s.td}>
                          <button type="button" onClick={() => deleteContractPrice(cp.product_id)} style={s.removeBtn} aria-label="Remove">
                            ✕
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Record info */}
        <div style={{ ...s.section, background: '#f9fafb' }}>
          <h3 style={s.sectionTitle}>Record information</h3>
          <div style={s.grid}>
            <Field label="Customer ID" value={customer._id || customer.id} />
            <Field label="Created" value={fmtDate(customer.created_at || customer.createdAt)} />
            <Field label="Last updated" value={fmtDate(customer.updated_at || customer.updatedAt)} />
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

const s: Record<string, React.CSSProperties> = {
  card: {
    background: '#fff',
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
    marginBottom: 30,
  },
  hero: {
    padding: '20px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 16,
    borderBottom: '1px solid #f3f4f6',
    background: '#fafafa',
  },
  bannerAvatar: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid #e5e7eb',
    flexShrink: 0,
  },
  bannerAvatarFallback: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: '#e3f2fd',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#0074d9',
    fontWeight: 800,
    fontSize: 22,
    flexShrink: 0,
  },
  statusPill: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: 999,
    fontWeight: 600,
    fontSize: 12,
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 1,
    background: '#e5e7eb',
    borderBottom: '1px solid #e5e7eb',
  },
  statCard: {
    background: '#fff',
    padding: '16px 20px',
    borderRight: '1px solid #f3f4f6',
  },
  statLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 6,
  },
  statValue: {
    fontSize: 20,
    fontWeight: 800,
    color: '#111827',
  },
  statSub: {
    fontSize: 11,
    color: '#9ca3af',
    marginTop: 4,
  },
  section: {
    padding: '22px 24px',
    borderTop: '1px solid #f0f0f0',
  },
  sectionTitle: {
    margin: '0 0 16px',
    color: '#111827',
    fontSize: 16,
    fontWeight: 700,
  },
  sectionHint: {
    color: '#6b7280',
    fontSize: 13,
    margin: '-8px 0 16px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '0 24px',
  },
  fieldLabel: {
    display: 'block',
    fontSize: 11,
    color: '#6b7280',
    fontWeight: 700,
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  notesBox: {
    background: '#f8fafc',
    padding: '14px 16px',
    borderRadius: 8,
    fontSize: 14,
    color: '#374151',
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    border: '1px solid #e5e7eb',
  },
  tag: {
    background: '#e3f2fd',
    color: '#0056a3',
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
  },
  attachmentLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 14px',
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    color: '#0074d9',
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 500,
  },
  emptyHint: {
    color: '#9ca3af',
    fontSize: 13,
    margin: 0,
  },
  contractForm: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'flex-end',
    marginBottom: 20,
    padding: 16,
    background: '#f8fafc',
    borderRadius: 10,
    border: '1px solid #e5e7eb',
  },
  formLabel: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 6,
  },
  tableWrap: {
    overflowX: 'auto',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: 640,
  },
  th: {
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 700,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  td: {
    padding: '12px 14px',
    fontSize: 14,
    color: '#1f2937',
  },
  removeBtn: {
    background: 'none',
    border: 'none',
    color: '#ef4444',
    cursor: 'pointer',
    fontSize: 16,
    padding: 4,
  },
};

export default CustomerView;
